import { Component, inject, OnInit, ChangeDetectorRef, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TimeLogService } from '../../../core/services/timelog.service';
import { ProjectService } from '../../../core/services/project.service';
import { AuthService } from '../../../core/services/auth.service';

@Component({
    selector: 'app-reports',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './reports.component.html',
    styleUrl: './reports.component.scss'
})
export class ReportsComponent implements OnInit, OnDestroy {
    private timeLogService = inject(TimeLogService);
    private projectService = inject(ProjectService);
    private authService = inject(AuthService);
    private cdr = inject(ChangeDetectorRef);

    logs: any[] = [];
    filteredLogs: any[] = [];
    timelineData: any[] = []; // { date: Date, users: { name: string, totalDuration: string, logs: any[], expanded: boolean }[], expanded: boolean }

    isAdmin = false;
    allProjects: any[] = [];
    allUserNames: string[] = [];
    isLoading = true;
    isLoadingMore = false;
    errorMessage = '';

    // Infinite scroll - date window loading
    currentWeekOffset = 0; // 0 = current window, 1 = previous window, etc.
    hasMoreData = true;
    pageSize = 1000; // Large page size to get all data for a window
    private readonly windowDays = 11;
    private scrollThrottleTimer: any = null;
    private emptyWindowCount = 0;
    private readonly maxEmptyWindows = 2;

    // Filter states
    showFilters = false;
    selectedUserName = '';
    selectedProjectId = '';
    startDate = '';
    endDate = '';

    // Modal state
    selectedLog: any = null;
    showModal = false;

    ngOnInit() {
        this.loadLogs();
        this.loadProjects();
    }

    ngOnDestroy() {
        if (this.scrollThrottleTimer) {
            clearTimeout(this.scrollThrottleTimer);
        }
    }

    @HostListener('window:scroll', ['$event'])
    onScroll(event: Event) {
        // Throttle scroll events for better performance
        if (this.scrollThrottleTimer) {
            return;
        }

        this.scrollThrottleTimer = setTimeout(() => {
            this.scrollThrottleTimer = null;

            if (this.isLoadingMore || !this.hasMoreData) {
                if (this.isLoadingMore) {
                    console.log('Already loading more, skipping...');
                }
                if (!this.hasMoreData) {
                    console.log('No more data available');
                }
                return;
            }

            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
            const scrollPercentage = (scrollTop + windowHeight) / documentHeight;

            console.log('Scroll event:', {
                scrollTop,
                windowHeight,
                documentHeight,
                scrollPercentage: (scrollPercentage * 100).toFixed(2) + '%',
                currentWeekOffset: this.currentWeekOffset,
                hasMoreData: this.hasMoreData,
                isLoadingMore: this.isLoadingMore
            });

            // Load more when user scrolls to 90% of the page (lowered threshold for easier triggering)
            if (scrollPercentage > 0.9) {
                console.log('Triggering loadNextWeek, current offset:', this.currentWeekOffset);
                this.loadNextWeek();
            }
        }, 200); // Throttle to every 200ms
    }

    loadProjects() {
        this.projectService.getAll().subscribe({
            next: (projects) => {
                this.allProjects = projects;
                this.cdr.detectChanges();
            },
            error: (err) => console.error('Failed to load projects', err)
        });
    }

    loadLogs() {
        this.isLoading = true;
        this.currentWeekOffset = 0;
        this.logs = [];
        this.hasMoreData = true;
        this.emptyWindowCount = 0;

        this.loadWeekData(0, false);
    }

    loadNextWeek() {
        if (this.isLoadingMore || !this.hasMoreData) {
            return;
        }

        this.currentWeekOffset++;
        this.loadWeekData(this.currentWeekOffset, true);
    }

    loadWeekData(weekOffset: number, append: boolean = false) {
        if (append) {
            this.isLoadingMore = true;
        } else {
            this.isLoading = true;
        }

        // Calculate date range for the specific window
        // window 0 = today inclusive, last 11 days: today to today - 10 days
        // window 1 = previous window (today - 11 days to today - 21 days)
        // window 2 = previous window (today - 22 days to today - 32 days)
        // etc.
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - (weekOffset * this.windowDays));
        endDate.setHours(23, 59, 59, 999);

        const startDate = new Date();
        startDate.setDate(startDate.getDate() - (weekOffset * this.windowDays) - (this.windowDays - 1));
        startDate.setHours(0, 0, 0, 0);

        console.log(`Loading week ${weekOffset}:`, {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            append
        });

        this.timeLogService.getAllLogs(1, this.pageSize, startDate, endDate).subscribe({
            next: (data: any) => {
                let items: any[] = [];

                if (Array.isArray(data)) {
                    items = data;
                } else if (data?.logs) {
                    items = data.logs;
                } else {
                    items = data?.data || data?.items || [];
                }

                console.log(`Week ${weekOffset} loaded:`, items.length, 'items');

                const mappedItems = items.map((log: any) => ({
                    ...log,
                    displayDuration: log.duration ? this.formatISO8601Duration(log.duration) : '0h'
                }));

                if (append) {
                    // Append new items to existing logs
                    this.logs = [...this.logs, ...mappedItems];
                } else {
                    // Replace logs (initial load)
                    this.logs = mappedItems;
                }

                if (items.length === 0) {
                    this.emptyWindowCount += 1;
                    if (this.emptyWindowCount >= this.maxEmptyWindows) {
                        this.hasMoreData = false;
                        console.log('No data found for consecutive windows, stopping');
                    }
                } else {
                    this.emptyWindowCount = 0;
                }

                // Extract unique user names for filter dropdown
                this.allUserNames = [...new Set(this.logs.map(log => log.userName).filter(name => !!name))].sort();

                this.applyFilters();
                this.checkUserRole();
                this.isLoading = false;
                this.isLoadingMore = false;
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Failed to load logs', err);
                this.errorMessage = 'Failed to load logs. Please try again.';
                this.isLoading = false;
                this.isLoadingMore = false;
                // Don't set hasMoreData to false on error, allow retry
                this.cdr.detectChanges();
            }
        });
    }

    // Reset and reload from beginning
    resetAndReload() {
        this.loadLogs();
    }

    applyFilters() {
        this.filteredLogs = this.logs.filter(log => {
            const matchesUser = !this.selectedUserName || log.userName === this.selectedUserName;
            const matchesProject = !this.selectedProjectId || log.projectId === this.selectedProjectId;

            let matchesDate = true;
            if (this.startDate || this.endDate) {
                const logDate = new Date(log.date);
                logDate.setHours(0, 0, 0, 0);

                if (this.startDate) {
                    const start = new Date(this.startDate);
                    start.setHours(0, 0, 0, 0);
                    if (logDate < start) matchesDate = false;
                }

                if (this.endDate) {
                    const end = new Date(this.endDate);
                    end.setHours(23, 59, 59, 999);
                    if (logDate > end) matchesDate = false;
                }
            }

            return matchesUser && matchesProject && matchesDate;
        });

        if (this.isAdmin) {
            this.processTimelineData();
        }

        this.cdr.detectChanges();
    }

    clearFilters() {
        this.selectedUserName = '';
        this.selectedProjectId = '';
        this.startDate = '';
        this.endDate = '';
        this.applyFilters();
    }

    openDetail(logId: string) {
        this.isLoading = true;
        this.timeLogService.getById(logId).subscribe({
            next: (log) => {
                this.selectedLog = {
                    ...log,
                    displayDuration: log.duration ? this.formatISO8601Duration(log.duration) : '0h'
                };
                this.showModal = true;
                this.isLoading = false;
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Failed to load log details', err);
                this.isLoading = false;
                this.cdr.detectChanges();
            }
        });
    }

    closeModal() {
        this.showModal = false;
        this.selectedLog = null;
    }

    private formatISO8601Duration(duration: string): string {
        const hoursMatch = duration.match(/(\d+)H/);
        const minutesMatch = duration.match(/(\d+)M/);

        const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
        const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;

        const parts = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);

        return parts.length > 0 ? parts.join(' ') : '0h';
    }

    checkUserRole() {
        const role = this.authService.getUserRole();
        this.isAdmin = role === 'SystemAdmin' || role === 'Admin';
        if (this.isAdmin) {
            this.processTimelineData();
        }
    }

    // Premium color palette for timeline headers (~50 colors)
    private groupColors = [
        '#ff7900', // Brand Orange
        '#3b82f6', // Blue 500
        '#10b981', // Emerald 500
        '#8b5cf6', // Violet 500
        '#f43f5e', // Rose 500
        '#0ea5e9', // Sky 500
        '#eab308', // Yellow 500
        '#6366f1', // Indigo 500
        '#ec4899', // Pink 500
        '#14b8a6', // Teal 500
        '#f97316', // Orange 500
        '#84cc16', // Lime 500
        '#06b6d4', // Cyan 500
        '#a855f7', // Purple 500
        '#d946ef', // Fuchsia 500
        '#ef4444', // Red 500
        '#f59e0b', // Amber 500
        '#22c55e', // Green 500
        '#2563eb', // Blue 600
        '#7c3aed', // Violet 600
        '#db2777', // Pink 600
        '#ea580c', // Orange 600
        '#65a30d', // Lime 600
        '#0891b2', // Cyan 600
        '#9333ea', // Purple 600
        '#c026d3', // Fuchsia 600
        '#dc2626', // Red 600
        '#d97706', // Amber 600
        '#16a34a', // Green 600
        '#4f46e5', // Indigo 600
        '#be123c', // Rose 700
        '#0284c7', // Sky 600
        '#b45309', // Amber 700
        '#4d7c0f', // Lime 700
        '#0e7490', // Cyan 700
        '#7e22ce', // Purple 700
        '#a21caf', // Fuchsia 700
        '#b91c1c', // Red 700
        '#c2410c', // Orange 700
        '#15803d', // Green 700
        '#1d4ed8', // Blue 700
        '#6d28d9', // Violet 700
        '#be185d', // Pink 700
        '#0369a1', // Sky 700
        '#0f766e', // Teal 700
        '#4338ca', // Indigo 700
        '#e11d48', // Rose 600
        '#fb923c', // Orange 400
        '#38bdf8', // Sky 400
        '#818cf8', // Indigo 400
        '#4ade80', // Green 400
        '#a78bfa', // Violet 400
        '#f472b6'  // Pink 400
    ];

    processTimelineData() {
        // Group logs by Date
        const groupedByDate = this.filteredLogs.reduce((acc, log) => {
            const dateStr = new Date(log.date).toISOString().split('T')[0];
            if (!acc[dateStr]) {
                acc[dateStr] = [];
            }
            acc[dateStr].push(log);
            return acc;
        }, {} as any);

        // Map to store consistent colors for users across dates
        const userColorMap: { [key: string]: string } = {};
        let colorIndex = 0;

        // Transform into array and sort by date descending
        this.timelineData = Object.keys(groupedByDate)
            .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
            .map(date => {
                const logsForDate = groupedByDate[date];

                // Group by User within Date
                const groupedByUser = logsForDate.reduce((acc: any, log: any) => {
                    const userName = log.userName || 'Unknown User';
                    if (!acc[userName]) {

                        // Assign consistent color
                        if (!userColorMap[userName]) {
                            userColorMap[userName] = this.groupColors[colorIndex % this.groupColors.length];
                            colorIndex++;
                        }

                        acc[userName] = {
                            name: userName,
                            logs: [],
                            totalDurationMinutes: 0,
                            projectDurations: {} as { [key: string]: number },
                            avatarLetter: (userName.charAt(0) || 'U').toUpperCase(),
                            color: userColorMap[userName]
                        };
                    }
                    acc[userName].logs.push(log);

                    const durationMins = this.parseDurationToMinutes(log.duration);
                    acc[userName].totalDurationMinutes += durationMins;

                    // Aggregate per project
                    const projectName = log.projectName || 'Unknown Project';
                    if (!acc[userName].projectDurations[projectName]) {
                        acc[userName].projectDurations[projectName] = 0;
                    }
                    acc[userName].projectDurations[projectName] += durationMins;

                    return acc;
                }, {});

                const users = Object.values(groupedByUser).map((user: any) => {
                    // Format project chips data
                    const projects = Object.keys(user.projectDurations).map(projName => ({
                        name: this.getProjectShortName(projName),
                        duration: this.formatMinutesToDuration(user.projectDurations[projName])
                    }));

                    const projectStrings = projects.map(p => `${p.name} ${p.duration}`);

                    // Create the full header string: "Name Surname | Project A 1h | Project B 2h"
                    const headerDisplay = [user.name, ...projectStrings].join(' | ');

                    return {
                        ...user,
                        headerDisplay: headerDisplay,
                        projects: projects,
                        totalDuration: this.formatMinutesToDuration(user.totalDurationMinutes),
                        expanded: false
                    };
                });

                return {
                    date: new Date(date),
                    users: users,
                    expanded: true
                };
            });
    }

    private getProjectShortName(name: string): string {
        const mapping: { [key: string]: string } = {
            'SoreLogic': 'SORE',
            'ECG Analysis': 'ECG'
        };
        return mapping[name] || name;
    }

    private parseDurationToMinutes(duration: string): number {
        if (!duration) return 0;
        const hoursMatch = duration.match(/(\d+)H/);
        const minutesMatch = duration.match(/(\d+)M/);
        const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
        const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
        return hours * 60 + minutes;
    }

    private formatMinutesToDuration(totalMinutes: number): string {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        const parts = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);

        return parts.length > 0 ? parts.join(' ') : '0h';
    }

    toggleDate(dateItem: any) {
        dateItem.expanded = !dateItem.expanded;
    }

    toggleUser(userItem: any, event: Event) {
        event.stopPropagation();
        userItem.expanded = !userItem.expanded;
    }
}
